import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { DocumentTable } from "@/components/DocumentTable";
import { FilterSystem } from "@/components/FilterSystem";
import { MetricsDashboard } from "@/components/MetricsDashboard";
import { Pagination } from "@/components/Pagination";
import { SelectAllBanner } from "@/components/SelectAllBanner";
import { SelectionToolbar } from "@/components/SelectionToolbar";
import { getStatusIdsForTab, StatusTabs, TabValue } from "@/components/StatusTabs";
import { TopNav } from "@/components/TopNav";
import { useAuth } from "@/context/AuthContext";
import { useBulkDelete } from "@/hooks/useBulkDelete";
import { useDeleteDocument, useDocuments, useUploadDocument } from "@/hooks/useDocuments";
import { useFilters } from "@/hooks/useFilters";
import { usePagination } from "@/hooks/usePagination";
import { useSelection } from "@/hooks/useSelection";
import { mapSortFieldToBackend, useSort } from "@/hooks/useSort";
import { getAllDocumentIds } from "@/services/documentsApi";
import { DOCUMENT_TYPE_OPTIONS } from "@/types/documentTypes";
import { fetchAuthSession } from "aws-amplify/auth";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate, useSearchParams } from "react-router-dom";
import Webcam from "react-webcam";

export function Dashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [isWebcamOpen, setIsWebcamOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("UNKNOWN");
  const webcamRef = useRef<Webcam>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Active tab state from URL (persisted for navigation)
  const activeTab = (searchParams.get("tab") as TabValue) || "processed";
  const setActiveTab = useCallback(
    (tab: TabValue) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (tab === "processed") {
            params.delete("tab"); // Clean URL for default tab
          } else {
            params.set("tab", tab);
          }
          // Reset to page 1 when tab changes
          params.delete("page");
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // New filter system state - default to "today" date filter on initial load
  const {
    filters: activeFilters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    buildQueryParams,
  } = useFilters({ defaultToToday: true });

  // Get status IDs for the current tab
  const statusIds = getStatusIdsForTab(activeTab);

  // Build filters object from new filter system
  const queryParams = buildQueryParams();

  // Build metrics filters from date range (for Complete and Review counts)
  // Note: "In Process" count is intentionally NOT filtered by date in the backend
  const metricsFilters = useMemo(
    () => ({
      startDate: queryParams.startDate || undefined,
      endDate: queryParams.endDate || undefined,
    }),
    [queryParams.startDate, queryParams.endDate]
  );

  // Pagination state - default to 100, persisted in localStorage
  const {
    page,
    pageSize,
    totalPages: _totalPages,
    setPage,
    setPageSize,
  } = usePagination({
    initialPage: 1,
    initialPageSize: 100,
  });
  void _totalPages; // We use actualTotalPages from API response instead

  // Sorting state - persisted in URL
  const { sortField, sortDirection, setSort } = useSort();

  // Build filters including pagination and sorting
  // Note: proNumberLike and proNumbers are mutually exclusive:
  // - Single PRO input → proNumberLike (partial match)
  // - Multiple comma-separated PROs → proNumbers (exact match)
  const filters = useMemo(
    () => ({
      documentType: queryParams.documentType || undefined,
      documentTypes: queryParams.documentTypes || undefined,
      proNumberLike: queryParams.proNumberLike || undefined,
      proNumbers: queryParams.proNumbers || undefined,
      shipmentId: queryParams.shipmentId || undefined,
      shipmentIds: queryParams.shipmentIds || undefined,
      statusIds: statusIds,
      startDate: queryParams.startDate || undefined,
      endDate: queryParams.endDate || undefined,
      page,
      pageSize,
      sortBy: mapSortFieldToBackend(sortField),
      sortDir: sortDirection,
    }),
    [queryParams, statusIds, page, pageSize, sortField, sortDirection]
  );

  // Use 5-second refresh for processing tab, 30-second for processed
  const refreshInterval = activeTab === "processing" ? 5000 : 30000;

  // Fetch documents with polling and filters
  const { data: documentResponse, isLoading, error } = useDocuments(refreshInterval, filters);

  // Extract documents and pagination info from response
  const documents = useMemo(() => documentResponse?.documents ?? [], [documentResponse?.documents]);
  const total = documentResponse?.total ?? 0;
  const actualTotalPages = documentResponse?.totalPages ?? 1;

  // Calculate display indices based on actual total from API
  const displayStartIndex = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const displayEndIndex = Math.min(page * pageSize, total);

  // Get page item IDs and disabled IDs for selection
  const pageItemIds = useMemo(() => documents.map((doc) => doc.id), [documents]);
  const disabledIds = useMemo(
    () =>
      new Set(
        documents
          .filter(
            (doc) =>
              doc.status === "processing" || doc.status === "uploading" || doc.status === "uploaded"
          )
          .map((doc) => doc.id)
      ),
    [documents]
  );

  // Selection state
  const {
    selectedIds,
    isAllPageSelected,
    isAllFilteredSelected,
    toggleAllOnPage,
    selectAllFiltered,
    clearSelection,
    handleRowClick,
    selectionCount,
  } = useSelection({
    pageItemIds,
    disabledIds,
  });

  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [resetAllDialogOpen, setResetAllDialogOpen] = useState(false);
  const [isResettingAll, setIsResettingAll] = useState(false);
  const { bulkDelete, isDeleting: isBulkDeleting } = useBulkDelete({
    onSuccess: (response) => {
      if (response.failed_count === 0) {
        enqueueSnackbar(`Successfully deleted ${response.deleted_count} document(s)`, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(
          `Deleted ${response.deleted_count} document(s), ${response.failed_count} failed`,
          { variant: "warning" }
        );
      }
      clearSelection();
      setBulkDeleteDialogOpen(false);
      setResetAllDialogOpen(false);
      setIsResettingAll(false);
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to delete documents: ${error.message}`, { variant: "error" });
      setIsResettingAll(false);
    },
  });

  // Handle bulk delete confirmation
  const handleBulkDeleteConfirm = (hardDelete: boolean) => {
    const idsToDelete = Array.from(selectedIds).map((id) => parseInt(id, 10));
    bulkDelete(idsToDelete, hardDelete);
  };

  // Handle reset all - delete all documents matching current filters
  const handleResetAllConfirm = async (hardDelete: boolean) => {
    try {
      setIsResettingAll(true);
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      // Build filters without pagination
      const resetFilters = {
        documentType: queryParams.documentTypes?.[0] || undefined,
        proNumber: queryParams.proNumberLike || undefined,
        shipmentIds: queryParams.shipmentIds || undefined,
        statusIds: statusIds,
        startDate: queryParams.startDate || undefined,
        endDate: queryParams.endDate || undefined,
      };

      // Get all document IDs matching filters
      const allIds = await getAllDocumentIds(accessToken, resetFilters);

      if (allIds.length === 0) {
        enqueueSnackbar("No documents to delete", { variant: "info" });
        setResetAllDialogOpen(false);
        setIsResettingAll(false);
        return;
      }

      // Delete all documents
      bulkDelete(allIds, hardDelete);
    } catch (error) {
      enqueueSnackbar(
        `Failed to reset documents: ${error instanceof Error ? error.message : "Unknown error"}`,
        { variant: "error" }
      );
      setIsResettingAll(false);
    }
  };

  // Clear selection on page, pageSize, or filter changes
  useEffect(() => {
    clearSelection();
  }, [page, pageSize, activeTab, clearSelection]);

  // Also clear when filters change (using a stable reference)
  const filterKey = JSON.stringify(queryParams);
  useEffect(() => {
    clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Handle page boundary - if current page > total pages, go to last page
  // Only do this when not loading to avoid race conditions with rapid page changes
  useEffect(() => {
    if (!isLoading && page > actualTotalPages && actualTotalPages > 0) {
      setPage(actualTotalPages);
    }
  }, [page, actualTotalPages, setPage, isLoading]);

  // Upload mutation
  const uploadMutation = useUploadDocument();

  // Single delete mutation (for individual document delete)
  const deleteMutation = useDeleteDocument();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<{
    id: string;
    fileName: string;
  } | null>(null);

  // Handle single delete - open modal
  const handleDeleteDocument = (documentId: string, fileName: string) => {
    setDocumentToDelete({ id: documentId, fileName });
    setDeleteModalOpen(true);
  };

  // Confirm delete with selected mode
  const confirmDelete = (hardDelete: boolean) => {
    if (!documentToDelete) return;

    deleteMutation.mutate(
      { documentId: documentToDelete.id, hardDelete },
      {
        onSuccess: () => {
          enqueueSnackbar(
            `Successfully ${hardDelete ? "permanently deleted" : "archived"} ${documentToDelete.fileName}`,
            { variant: "success" }
          );
          setDeleteModalOpen(false);
          setDocumentToDelete(null);
        },
        onError: (error) => {
          enqueueSnackbar(
            `Failed to delete document: ${error instanceof Error ? error.message : "Unknown error"}`,
            { variant: "error" }
          );
        },
      }
    );
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setDocumentToDelete(null);
  };

  // Extract first name from access-control API user info
  const getFirstName = () => {
    if (user?.firstName) {
      return user.firstName;
    }
    if (user?.name) {
      return user.name.split(" ")[0];
    }
    if (user?.email) {
      return user.email.split("@")[0];
    }
    return "User";
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        // Track upload progress per file
        const fileId = `${file.name}-${Date.now()}`;

        uploadMutation.mutate(
          {
            file,
            documentType: selectedDocumentType,
            onProgress: (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [fileId]: progress,
              }));
            },
          },
          {
            onSuccess: () => {
              enqueueSnackbar(`Successfully uploaded ${file.name}`, {
                variant: "success",
              });
              setUploadProgress((prev) => {
                const newProgress = { ...prev };
                delete newProgress[fileId];
                return newProgress;
              });
            },
            onError: (error) => {
              enqueueSnackbar(`Failed to upload ${file.name}: ${error.message}`, {
                variant: "error",
              });
              setUploadProgress((prev) => {
                const newProgress = { ...prev };
                delete newProgress[fileId];
                return newProgress;
              });
            },
          }
        );
      });
    },
    [uploadMutation, enqueueSnackbar, selectedDocumentType]
  );

  // Capture photo from webcam
  const capturePhoto = useCallback(() => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (!imageSrc) {
      enqueueSnackbar("Failed to capture photo", { variant: "error" });
      return;
    }

    // Convert base64 to file
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        const fileId = `${file.name}-${Date.now()}`;

        uploadMutation.mutate(
          {
            file,
            documentType: selectedDocumentType,
            onProgress: (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [fileId]: progress,
              }));
            },
          },
          {
            onSuccess: () => {
              enqueueSnackbar("Successfully uploaded photo", {
                variant: "success",
              });
              setUploadProgress((prev) => {
                const newProgress = { ...prev };
                delete newProgress[fileId];
                return newProgress;
              });
              setIsWebcamOpen(false);
            },
            onError: (error) => {
              enqueueSnackbar(`Failed to upload photo: ${error.message}`, {
                variant: "error",
              });
              setUploadProgress((prev) => {
                const newProgress = { ...prev };
                delete newProgress[fileId];
                return newProgress;
              });
            },
          }
        );
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to process photo: ${error.message}`, {
          variant: "error",
        });
      });
  }, [webcamRef, uploadMutation, selectedDocumentType, enqueueSnackbar]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    multiple: true,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark">
      {/* Top Navigation */}
      <TopNav />

      {/* Main Content */}
      <main className="flex-1 p-4 sm:p-6 md:p-8">
        <div className="mx-auto max-w-[960px]">
          {/* Welcome Section */}
          <div className="flex flex-wrap justify-between gap-4 py-4">
            <div className="flex min-w-72 flex-col gap-2">
              <p className="text-4xl font-black leading-tight tracking-[-0.033em] text-black dark:text-white">
                Hi, {getFirstName()}!
              </p>
              <p className="text-base font-normal leading-normal text-gray-600 dark:text-[#92a4c9]">
                Welcome to the Intelligent Document Processing Hub. Get started by uploading a
                document.
              </p>
            </div>
          </div>

          {/* Metrics Dashboard - Task 14.1 */}
          {/* Filters apply to Complete and Review counts; In Process always shows global queue depth */}
          <MetricsDashboard className="mb-6" filters={metricsFilters} />

          {/* Document Type Selector */}
          <div className="py-4">
            <label
              htmlFor="documentType"
              className="mb-2 block text-sm font-medium text-gray-700 dark:text-white"
            >
              Document Type
            </label>
            <select
              id="documentType"
              value={selectedDocumentType}
              onChange={(e) => setSelectedDocumentType(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-black focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-[#324467] dark:bg-[#192233] dark:text-white"
            >
              {DOCUMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select the document type before uploading. This helps with proper categorization and
              processing.
            </p>
          </div>

          {/* Upload Options */}
          <div className="grid grid-cols-1 gap-4 py-8 md:grid-cols-2 md:gap-6">
            {/* Webcam Capture */}
            <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-6 dark:border-[#232f48] dark:bg-[#192233]/50">
              <span className="material-symbols-outlined text-4xl text-primary">photo_camera</span>
              <div>
                <p className="text-lg font-medium leading-normal text-black dark:text-white">
                  Capture Document
                </p>
                <p className="text-sm font-normal leading-normal text-gray-600 dark:text-[#92a4c9]">
                  Use your camera to capture a document instantly.
                </p>
              </div>
              <button
                onClick={() => setIsWebcamOpen(!isWebcamOpen)}
                className="mt-4 flex h-10 items-center justify-center rounded-lg bg-primary px-4 text-sm font-bold text-white"
              >
                {isWebcamOpen ? "Close Camera" : "Launch Camera"}
              </button>

              {/* Webcam View */}
              {isWebcamOpen && (
                <div className="mt-4 space-y-3">
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    className="w-full rounded-lg"
                    videoConstraints={{
                      width: 1280,
                      height: 720,
                      facingMode: "environment",
                    }}
                  />
                  <button
                    onClick={capturePhoto}
                    className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-bold text-white hover:bg-primary/90"
                  >
                    <span className="material-symbols-outlined">photo_camera</span>
                    Capture & Upload
                  </button>
                </div>
              )}
            </div>

            {/* File Upload */}
            <div
              {...getRootProps()}
              className={`flex flex-col gap-3 rounded-xl border-2 border-dashed p-6 ${
                isDragActive
                  ? "border-primary bg-primary/10"
                  : "border-gray-300 bg-white dark:border-[#324467] dark:bg-[#192233]/50"
              } cursor-pointer transition-colors`}
            >
              <input {...getInputProps()} />
              <span className="material-symbols-outlined text-4xl text-primary">upload_file</span>
              <div>
                <p className="text-lg font-medium leading-normal text-black dark:text-white">
                  Upload Documents
                </p>
                <p className="text-sm font-normal leading-normal text-gray-600 dark:text-[#92a4c9]">
                  {isDragActive
                    ? "Drop files here..."
                    : "Drag & drop one or more files (PDF, JPG, PNG, TIFF) here."}
                </p>
              </div>
              <button
                type="button"
                className="mt-4 flex h-10 items-center justify-center rounded-lg bg-primary/20 px-4 text-sm font-bold text-primary dark:bg-primary/30"
              >
                Click to browse
              </button>
            </div>
          </div>

          {/* Upload Progress */}
          {Object.keys(uploadProgress).length > 0 && (
            <div className="mt-6">
              <h3 className="mb-3 text-lg font-semibold text-black dark:text-white">
                Uploading Files
              </h3>
              <div className="space-y-3">
                {Object.entries(uploadProgress).map(([fileId, progress]) => (
                  <div
                    key={fileId}
                    className="rounded-lg border border-gray-200 bg-white p-4 dark:border-[#324467] dark:bg-[#192233]/50"
                  >
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="font-medium text-black dark:text-white">
                        {fileId.split("-")[0]}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                      <div
                        className="h-full bg-primary transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Document List Section */}
          <div className="mt-8">
            {/* Status Tabs - Task 14.2 */}
            <StatusTabs
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab)}
              className="mb-3"
            />

            {/* Filter System - Task 14.3 */}
            <div className="rounded-t-lg border border-b-0 border-gray-200 bg-gray-50/50 dark:border-[#324467] dark:bg-[#192233]/30">
              <FilterSystem
                filters={activeFilters}
                onAddFilter={addFilter}
                onUpdateFilter={updateFilter}
                onRemoveFilter={removeFilter}
                onClearFilters={clearFilters}
              />
            </div>

            {/* Selection Toolbar */}
            <SelectionToolbar
              selectedCount={selectionCount}
              totalFilteredCount={total}
              onDeleteClick={() => setBulkDeleteDialogOpen(true)}
              onResetAllClick={() => setResetAllDialogOpen(true)}
              isDeleting={isBulkDeleting || isResettingAll}
            />

            {/* Select All Banner */}
            <SelectAllBanner
              selectedCount={selectionCount}
              pageSelectedCount={documents.filter((doc) => selectedIds.has(doc.id)).length}
              totalFilteredCount={total}
              isAllFilteredSelected={isAllFilteredSelected}
              onSelectAllFiltered={() => {
                // For now, select all on current page since we'd need all IDs
                // In a real implementation, this would fetch all document IDs
                selectAllFiltered(pageItemIds);
              }}
              onClearSelection={clearSelection}
            />

            {/* Document Table */}
            <DocumentTable
              documents={documents}
              isLoading={isLoading}
              error={error}
              selectedIds={selectedIds}
              isAllSelected={isAllPageSelected}
              disabledIds={disabledIds}
              onToggleAll={toggleAllOnPage}
              onRowClick={handleRowClick}
              onViewDocument={(id) => navigate(`/documents/${id}`)}
              onDeleteDocument={handleDeleteDocument}
              isDeleting={deleteMutation.isPending}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={setSort}
            />

            {/* Pagination */}
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={actualTotalPages}
              startIndex={displayStartIndex}
              endIndex={displayEndIndex}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        </div>
      </main>

      {/* Bulk Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={bulkDeleteDialogOpen}
        documentCount={selectionCount}
        isDeleting={isBulkDeleting}
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteDialogOpen(false)}
      />

      {/* Reset All Confirmation Dialog */}
      <DeleteConfirmDialog
        open={resetAllDialogOpen}
        documentCount={total}
        isDeleting={isResettingAll || isBulkDeleting}
        onConfirm={handleResetAllConfirm}
        onCancel={() => setResetAllDialogOpen(false)}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && documentToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  Delete Document
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Choose how to delete{" "}
                  <span className="font-semibold">{documentToDelete.fileName}</span>
                </p>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-6">
              {/* Soft Delete Option */}
              <div className="border-2 border-slate-200 dark:border-slate-700 rounded-lg p-4 hover:border-primary dark:hover:border-primary transition-colors">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                    />
                  </svg>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 dark:text-white text-sm">
                      Archive (Recommended)
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      Hides the document from view but keeps it for audit trail and compliance. Can
                      be restored if needed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Hard Delete Option */}
              <div className="border-2 border-red-200 dark:border-red-900/50 rounded-lg p-4 hover:border-red-500 dark:hover:border-red-500 transition-colors">
                <div className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-600 dark:text-red-400 text-sm">
                      Permanent Delete
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      Permanently removes the document from database and S3 storage.
                      <span className="font-semibold text-red-600 dark:text-red-400">
                        {" "}
                        This cannot be undone!
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={cancelDelete}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2.5 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDelete(false)}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleteMutation.isPending ? (
                  <>
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Deleting...</span>
                  </>
                ) : (
                  "Archive"
                )}
              </button>
              <button
                onClick={() => confirmDelete(true)}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
